import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "product-overview-media" }
const _hoisted_2 = { class: "product-overview-media__carousel__carousel-wapper" }
const _hoisted_3 = {
  key: 1,
  class: "product-overview-media__carousel-video"
}
const _hoisted_4 = ["src"]
const _hoisted_5 = ["src"]
const _hoisted_6 = /*#__PURE__*/_createTextVNode(" Your browser does not support the video tag. ")
const _hoisted_7 = /*#__PURE__*/_createElementVNode("div", { class: "product-overview-media__grey-background" }, null, -1)
const _hoisted_8 = { class: "product-overview-media__arrows" }
const _hoisted_9 = ["aria-label"]
const _hoisted_10 = {
  key: 1,
  class: "product-overview-media__arrow-placeholder"
}
const _hoisted_11 = ["aria-label"]
const _hoisted_12 = {
  key: 3,
  class: "product-overview-media__arrow-placeholder"
}
const _hoisted_13 = ["href", "aria-label"]
const _hoisted_14 = /*#__PURE__*/_createElementVNode("span", { class: "fa fa-external-link" }, null, -1)
const _hoisted_15 = [
  _hoisted_14
]
const _hoisted_16 = {
  key: 0,
  class: "product-overview-media__slider-indicator"
}
const _hoisted_17 = ["innerHTML"]
const _hoisted_18 = ["onClick"]
const _hoisted_19 = ["src", "alt"]
const _hoisted_20 = ["src"]
const _hoisted_21 = ["src"]
const _hoisted_22 = /*#__PURE__*/_createTextVNode(" Your browser does not support the video tag. ")
const _hoisted_23 = /*#__PURE__*/_createElementVNode("div", { class: "product-overview-media__carousel-thumb-overlay" }, null, -1)
const _hoisted_24 = { class: "product-overview-media__thumb-arrows" }

import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onBeforeUpdate,
  onMounted,
  ref,
  watch,
} from 'vue';

import { toLitiumMediaUrlKey } from '@distancify/drapejs-litium/lib/keys';

import useSizeDisplay from '@/composables/useSizeDisplay';
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import useEventListener from '@/composables/useEventListener';

import { scrollIntoView } from '@/helpers/scroll-into-view';

import ContentCarousel from '../../ContentCarousel.vue';
import ZoomableImage from '../../ZoomableImage.vue';
import SliderIndicator from '@/components/SliderIndicator.vue';
import CarouselArrow from '../../svg/CarouselArrow.vue';

interface Props {
  images: string[];
  videos: Record<string, string>;
  canDisplayProductImagesOverview: boolean;
  alt: string;
}

interface Item {
  id: string;
  type: string;
  mp4?: string;
  webm?: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductOverviewMedia',
  props: {
    images: null,
    videos: null,
    canDisplayProductImagesOverview: { type: Boolean },
    alt: null
  },
  setup(__props: any) {

const props = __props as {
  images: string[];
  videos: Record<string, string>;
  canDisplayProductImagesOverview: boolean;
  alt: string;
}

const defaultMediaWidth = 750;
const maxZoomLevel = 2;



const toLitiumMediaUrl = inject(toLitiumMediaUrlKey);
const { product } = inject('addToCartContext') as any;

const { websiteText } = useWebsiteTexts();
const { getSizeName } = useSizeDisplay();
const { useWindowEventListener } = useEventListener();

const carousel = ref<InstanceType<typeof ContentCarousel> | null>(null);
const thumbListContentEl = ref<HTMLElement | null>(null);

const currentSlide = ref(0);
const zoomableImages = ref<any[]>([]);
const disableInfiniteSlider = ref(true);
const carouselHeight = ref(0);
const canScrollTop = ref(false);
const canScrollBottom = ref(false);

const items = computed<Item[]>(() => {
  const list: Item[] = props.images.map((i) => ({
    id: i,
    type: 'image',
  }));

  if (hasVideo.value) {
    list.splice(1, 0, {
      id: props.videos['video/mp4'],
      type: 'video',
      mp4: props.videos['video/mp4'],
      ...(props.videos?.['video/webm'] && {
        webm: props.videos['video/webm'],
      }),
    });
  }

  return list;
});

const activeCarouselItem = computed(() => {
  return items.value[currentSlide.value - 1];
});

const hasVideo = computed(() => props.videos?.['video/mp4']);

const slideCount = computed(() => items.value.length);

const displayLeftArrow = computed(() => {
  return disableInfiniteSlider.value ? currentSlide.value > 1 : true;
});

const displayRightArrow = computed(() => {
  return disableInfiniteSlider.value
    ? currentSlide.value < slideCount.value
    : true;
});

const variant = computed(() => {
  return {
    photomodelheight: product?.value?.photoModelInfo?.height,
    photomodelsize: getSizeName(product?.value?.photoModelInfo?.size),
  };
});

const photoModelInfo = computed(() => {
  if (!variant.value.photomodelheight || !variant.value.photomodelsize) {
    return '';
  }
  return `<span>${websiteText('sizeguide__photomodel_label').value}</span> ${
    websiteText('sizeguide__photomodel_value', {
      'variant.photoModelHeight': variant.value.photomodelheight,
      'variant.photoModelSize': variant.value.photomodelsize,
    }).value
  }`;
});

watch(items, () => {
  if (carousel.value) {
    carousel.value.reset();
    carousel.value.loadSlides();
    initializeCarouselVariables();
  }
}, { deep: true });

function addZoomableImage(el: any) {
  if (el) {
    zoomableImages.value.push(el);
  }
}

function slideDidChange(slide: number) {
  currentSlide.value = slide;
  resetZoomableImages();
  scrollActiveIntoView();
}

function previousSlide() {
  if (carousel.value) {
    carousel.value.go(-1);
  }
}

function nextSlide() {
  if (carousel.value) {
    carousel.value.go(1);
  }
}

function getImageUrl(imageId: string) {
  return toLitiumMediaUrl?.(imageId, { maxWidth: defaultMediaWidth });
}

function getHighResolutionImageUrl(imageId: string) {
  return toLitiumMediaUrl?.(imageId, {
    maxWidth: defaultMediaWidth * maxZoomLevel,
  });
}

function getFullMediaUrl(imageId: string) {
  return toLitiumMediaUrl?.(imageId);
}

function resetZoomableImages() {
  for (const zi of zoomableImages.value) {
    zi.resetZoom();
  }
}

function goToSlide(targetSlide: number) {
  if (carousel.value) {
    carousel.value.goToSlide(targetSlide);
  }
}

function isActiveSlide(idx: number) {
  return idx === currentSlide.value;
}

function getThumbImageUrl(imageId: string) {
  return toLitiumMediaUrl?.(imageId, { maxWidth: 94 });
}

async function scrollActiveIntoView() {
  await nextTick();
  const tumbList = document.querySelector(
    '.product-overview-media__carousel-tumb-list-content'
  );
  const active = document.querySelector(
    '.product-overview-media__carousel-thumb--active'
  );
  if (tumbList && active) {
    scrollIntoView(tumbList, active);
  }
}

async function initializeCarouselVariables() {
  await nextTick();
  carouselHeight.value = carousel.value?.$el.clientHeight || 0;
  handleThumbListScroll();
}

function handleThumbListScroll() {
  if (!thumbListContentEl.value) return;
  const scrollOffset = 2;

  const { scrollTop, scrollHeight, clientHeight } = thumbListContentEl.value;
  canScrollTop.value = scrollTop > 0;

  canScrollBottom.value =
    scrollTop + clientHeight + scrollOffset < scrollHeight;
}

watch(items, () => {
  handleThumbListScroll();
});

onMounted(() => {
  setTimeout(() => {
    handleThumbListScroll();
    thumbListContentEl.value?.addEventListener('scroll', handleThumbListScroll);
  }, 100);
});

useWindowEventListener('resize', initializeCarouselVariables, true);

onBeforeUpdate(() => {
  zoomableImages.value = [];
});

onBeforeUnmount(() => {
  thumbListContentEl.value?.removeEventListener(
    'scroll',
    handleThumbListScroll
  );
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(ContentCarousel, {
        class: "product-overview-media__carousel",
        ref_key: "carousel",
        ref: carousel,
        onSlideDidChange: slideDidChange,
        disableInfiniteSlider: disableInfiniteSlider.value
      }, {
        overlay: _withCtx(() => [
          _createElementVNode("div", _hoisted_8, [
            (_unref(displayLeftArrow))
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  class: "product-overview-media__arrow product-overview-media__arrow--left clean-button",
                  onClick: previousSlide,
                  "aria-label": _unref(websiteText)('global__previous').value
                }, [
                  _createVNode(CarouselArrow)
                ], 8, _hoisted_9))
              : (_openBlock(), _createElementBlock("div", _hoisted_10)),
            (_unref(displayRightArrow))
              ? (_openBlock(), _createElementBlock("button", {
                  key: 2,
                  class: "product-overview-media__arrow clean-button",
                  onClick: nextSlide,
                  "aria-label": _unref(websiteText)('global__next').value
                }, [
                  _createVNode(CarouselArrow)
                ], 8, _hoisted_11))
              : (_openBlock(), _createElementBlock("div", _hoisted_12))
          ]),
          (_unref(activeCarouselItem) && _unref(activeCarouselItem).type === 'image')
            ? (_openBlock(), _createElementBlock("a", {
                key: 0,
                href: getFullMediaUrl(_unref(activeCarouselItem).id as string),
                target: "_blank",
                class: "product-overview-media__carousel-item-pop-out",
                "aria-label": _unref(websiteText)('product_overview_media__open_full_image').value
              }, _hoisted_15, 8, _hoisted_13))
            : _createCommentVNode("", true)
        ]),
        default: _withCtx(() => [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(items), (mediaItem) => {
            return (_openBlock(), _createElementBlock("div", {
              key: mediaItem.id as string,
              ref_for: true,
              ref: "mediaSlide",
              class: "product-overview-media__carousel-item-container"
            }, [
              (mediaItem.type === 'image')
                ? (_openBlock(), _createBlock(ZoomableImage, {
                    key: 0,
                    class: "product-overview-media__carousel-image",
                    ref_for: true,
                    ref: addZoomableImage,
                    maxZoomLevel: maxZoomLevel,
                    unzoomedImageUrl: getImageUrl(mediaItem.id as string) || '',
                    zoomedImageUrl: getHighResolutionImageUrl(mediaItem.id as string),
                    alt: __props.alt
                  }, null, 8, ["unzoomedImageUrl", "zoomedImageUrl", "alt"]))
                : _createCommentVNode("", true),
              (mediaItem.type === 'video')
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    _createElementVNode("video", {
                      playsinline: "",
                      autoplay: "",
                      muted: "",
                      loop: "",
                      controls: "",
                      ref_for: true,
                      ref: "video"
                    }, [
                      _createElementVNode("source", {
                        src: getFullMediaUrl((mediaItem as any).mp4),
                        type: "video/mp4"
                      }, null, 8, _hoisted_4),
                      ((mediaItem as any).webm)
                        ? (_openBlock(), _createElementBlock("source", {
                            key: 0,
                            src: getFullMediaUrl((mediaItem as any).webm),
                            type: "video/webm"
                          }, null, 8, _hoisted_5))
                        : _createCommentVNode("", true),
                      _hoisted_6
                    ], 512)
                  ]))
                : _createCommentVNode("", true),
              _hoisted_7
            ]))
          }), 128))
        ]),
        _: 1
      }, 8, ["disableInfiniteSlider"]),
      (_unref(slideCount) > 1)
        ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
            _createVNode(SliderIndicator, {
              slides: _unref(slideCount),
              currentSlide: currentSlide.value
            }, null, 8, ["slides", "currentSlide"])
          ]))
        : _createCommentVNode("", true),
      (_unref(photoModelInfo))
        ? (_openBlock(), _createElementBlock("div", {
            key: 1,
            class: "product-overview-media__model",
            innerHTML: _unref(photoModelInfo)
          }, null, 8, _hoisted_17))
        : _createCommentVNode("", true)
    ]),
    (__props.canDisplayProductImagesOverview)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "product-overview-media__carousel-tumb-list",
          style: _normalizeStyle({
        maxHeight: `${carouselHeight.value}px`,
      })
        }, [
          _createElementVNode("div", {
            class: _normalizeClass(["product-overview-media__carousel-tumb-list-content", {
          'has-scroll-top': canScrollTop.value,
          'has-scroll-bottom': canScrollBottom.value,
        }]),
            style: _normalizeStyle({
          maxHeight: `${carouselHeight.value}px`,
        }),
            ref_key: "thumbListContentEl",
            ref: thumbListContentEl
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(items), (mediaItem, idx) => {
              return (_openBlock(), _createElementBlock("div", {
                key: mediaItem.id,
                class: "product-overview-media__carousel-thumb",
                onClick: ($event: any) => (goToSlide(idx + 1))
              }, [
                (mediaItem.type === 'image')
                  ? (_openBlock(), _createElementBlock("img", {
                      key: 0,
                      class: _normalizeClass(["product-overview-media__carousel-thumb-media", {
              'product-overview-media__carousel-thumb--active': isActiveSlide(
                idx + 1
              ),
            }]),
                      draggable: "false",
                      src: getThumbImageUrl(mediaItem.id as string),
                      alt: __props.alt
                    }, null, 10, _hoisted_19))
                  : _createCommentVNode("", true),
                (mediaItem.type === 'video')
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 1,
                      class: _normalizeClass(["product-overview-media__carousel-thumb-media", {
              'product-overview-media__carousel-thumb--active': isActiveSlide(
                idx + 1
              ),
            }])
                    }, [
                      _createElementVNode("video", {
                        playsinline: "",
                        autoplay: "",
                        muted: "",
                        loop: "",
                        ref_for: true,
                        ref: "videoThumb"
                      }, [
                        ((mediaItem as any).webm)
                          ? (_openBlock(), _createElementBlock("source", {
                              key: 0,
                              src: getFullMediaUrl((mediaItem as any).webm),
                              type: "video/webm"
                            }, null, 8, _hoisted_20))
                          : ((mediaItem as any).mp4)
                            ? (_openBlock(), _createElementBlock("source", {
                                key: 1,
                                src: getFullMediaUrl((mediaItem as any).mp4),
                                type: "video/mp4"
                              }, null, 8, _hoisted_21))
                            : _createCommentVNode("", true),
                        _hoisted_22
                      ], 512)
                    ], 2))
                  : _createCommentVNode("", true),
                _hoisted_23
              ], 8, _hoisted_18))
            }), 128))
          ], 6),
          _createElementVNode("div", _hoisted_24, [
            _createVNode(CarouselArrow, {
              tabindex: "0",
              class: "product-overview-media__arrow product-overview-media__arrow--top",
              onClick: previousSlide
            }),
            _createVNode(CarouselArrow, {
              tabindex: "0",
              class: "product-overview-media__arrow product-overview-media__arrow--bottom",
              onClick: nextSlide
            })
          ])
        ], 4))
      : _createCommentVNode("", true)
  ]))
}
}

})